import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

export interface PackCostDialogData {
    /** The figure the user just keyed. Treated as a PACK price when they tick the box. */
    enteredCost: number;
    /** Shown for context so they can see which part they are pricing. */
    partNumber?: string;
}

export interface PackCostDialogResult {
    /** What one unit costs, rounded to 4dp. This is the value to save. */
    unitCost: number;
    isPack: boolean;
    packQuantity: number;
}

/** Money to 4dp. EPSILON nudge so 0.15 * 100 style float error rounds the way a human expects. */
export function roundTo4dp(value: number): number {
    if (!isFinite(value)) return 0;
    return Math.round((value + Number.EPSILON) * 10000) / 10000;
}

/**
 * "Is that price for one, or for a box of them?" — asked at the moment a cost is entered
 * (Dave, 2026-08-13), because it is the only moment anyone knows the answer.
 *
 * Standalone per the CLAUDE.md rule: it opens from the part details form AND from the record-
 * quote dialog, which sit behind different lazy modules, and a module-declared dialog resolves
 * its directives inconsistently across that boundary (the symptom being inputs rendering as
 * raw text).
 */
@Component({
    selector: 'app-pack-cost-dialog',
    templateUrl: './pack-cost-dialog.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CurrencyPipe, FormsModule,
        MatButtonModule, MatCheckboxModule, MatDialogModule, MatFormFieldModule, MatInputModule,
    ],
})
export class PackCostDialogComponent {
    isPack = false;
    packQuantity: number | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: PackCostDialogData,
        private dialogRef: MatDialogRef<PackCostDialogComponent, PackCostDialogResult | null>,
    ) {}

    /** Live preview, and the value Confirm commits. A pack of 0 or 1 is just a single unit. */
    get unitCost(): number {
        const qty = Number(this.packQuantity);
        if (!this.isPack || !isFinite(qty) || qty <= 1) return roundTo4dp(this.data.enteredCost);
        return roundTo4dp(this.data.enteredCost / qty);
    }

    /** A ticked pack with no quantity is the one genuinely unanswerable state. */
    get canConfirm(): boolean {
        return !this.isPack || (Number(this.packQuantity) >= 2);
    }

    confirm(): void {
        if (!this.canConfirm) return;
        this.dialogRef.close({
            unitCost: this.unitCost,
            isPack: this.isPack,
            packQuantity: this.isPack ? Number(this.packQuantity) : 1,
        });
    }

    cancel(): void {
        this.dialogRef.close(null);
    }

    /**
     * Ask about a cost that has just been entered, resolving to the per-unit figure.
     * Resolves to null if the user backs out, which callers should treat as "leave the cost
     * as it was" rather than as a zero.
     */
    static ask(dialog: MatDialog, data: PackCostDialogData) {
        return dialog.open<PackCostDialogComponent, PackCostDialogData, PackCostDialogResult | null>(
            PackCostDialogComponent,
            // Width lives HERE and nowhere else. The template setting its own min-width wider
            // than this minus the surface padding is what produced a horizontal scrollbar.
            { data, width: '440px', maxWidth: '92vw', autoFocus: false, disableClose: true },
        ).afterClosed();
    }
}
